import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { gql } from '@apollo/client';
import { useFetchBookmarkedArticles } from './useFetchBookmarkedArticles';
import { useIntl } from 'react-intl';
import { cmsGraphql, useDeepEqualMemo } from '@xolv-mybrightlink/common';
import { Article, ArticleResponse } from '../models/ArticleResult';
import { useArticleContext, useArticleByCategory } from './useArticleContext';

const FETCH_ARTICLES = gql`
  query Posts($categories: [ID], $pageSize: Int!, $endCursor: String) {
    posts(
      where: { categoryIn: $categories }
      first: $pageSize
      after: $endCursor
    ) {
      nodes {
        id
        date
        slug
        title(format: RENDERED)
        content(format: RENDERED)
        databaseId
        categories {
          nodes {
            name
            databaseId
          }
        }
        featuredImage {
          node {
            sourceUrl
          }
        }
      }
      pageInfo {
        endCursor
      }
    }
  }
`;

type ArticleFetchResult =
  | { type: 'LOADING' }
  | { type: 'ERROR' }
  | { type: 'DATA'; articles: Article[]; nextCursor: string | null };

export const useFetchArticles = (categoryId: string) => {
  const [, dispatch] = useArticleContext();
  const { initial, endCursor, loading } = useArticleByCategory(categoryId);
  const getBookmarkedArticle = useFetchBookmarkedArticles();
  const intl = useIntl();

  return useCallback(
    async (pageSize?: number) => {
      if (loading || (!initial && !endCursor)) {
        return;
      }
      try {
        dispatch({ type: 'article-list-fetching', categoryId });
        const categories =
          categoryId === 'all-articles' ? [] : [Number(categoryId)];
        const resultArticle = await cmsGraphql.query<ArticleResponse>({
          query: FETCH_ARTICLES,
          variables: {
            pageSize: pageSize ?? 10,
            categories,
            endCursor,
          },
        });
        const resultArticleWithBookmarked = await getBookmarkedArticle(
          resultArticle.data.posts.nodes,
        );
        dispatch({
          type: 'article-list-fetched',
          articles: resultArticleWithBookmarked,
          endCursor: resultArticle.data.posts.pageInfo.endCursor,
          categoryId,
        });
      } catch (e) {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
        dispatch({
          type: 'article-list-error',
          categoryId,
        });
      }
    },
    [
      categoryId,
      initial,
      endCursor,
      dispatch,
      getBookmarkedArticle,
      loading,
      intl,
    ],
  );
};

export const useFetchPreviewArticles = (
  categoryId: string | undefined,
  endCursor: string | null,
  pageSize = 10,
  retry?: boolean,
) => {
  const [result, setResult] = useState<ArticleFetchResult>({ type: 'LOADING' });
  const intl = useIntl();
  const getBookmarkedArticle = useFetchBookmarkedArticles();
  useEffect(() => {
    const fetch = async () => {
      try {
        setResult({ type: 'LOADING' });
        const categories = categoryId !== undefined ? [Number(categoryId)] : [];
        const resultArticle = await cmsGraphql.query<ArticleResponse>({
          query: FETCH_ARTICLES,
          variables: { pageSize: pageSize, categories, endCursor },
        });
        const resultArticleWithBookmarked = await getBookmarkedArticle(
          resultArticle.data.posts.nodes,
        );
        setResult({
          type: 'DATA',
          articles: resultArticleWithBookmarked,
          nextCursor: resultArticle.data.posts.pageInfo.endCursor,
        });
      } catch (e) {
        toast(intl.formatMessage({ id: 'common.error.message' }), {
          toastId: 'common.error.message',
        });
        setResult({ type: 'ERROR' });
      }
    };
    fetch();
  }, [endCursor, categoryId, pageSize, intl, getBookmarkedArticle, retry]);
  return useDeepEqualMemo(result);
};
